// TAMA 38 Policy Extractor
// Extracts municipality policy data directly from existing iPLAN integration

import { YesNoUnknownValues } from '../types/feasibility.js';

/**
 * Extract comprehensive TAMA 38 policy data from iPLAN planning documents
 * This leverages our existing iPLAN integration to get real-time policy status
 */
class Tama38PolicyExtractor {
  
  /**
   * Extract TAMA 38 policy status from planning documents
   * @param {Object[]} plans - iPLAN planning data (already fetched)
   * @param {string} municipalityName - Municipality name
   * @returns {Object} Comprehensive TAMA 38 policy data
   */
  static extractTama38Policy(plans = [], municipalityName) {
    console.log(`🎯 Extracting TAMA 38 policy from ${plans.length} iPLAN documents for ${municipalityName}`);
    
    const policyData = {
      municipalityName,
      tama38Status: YesNoUnknownValues.UNKNOWN,
      tama38ValidUntil: null,
      activeTama38Plans: 0,
      recentTama38Activity: false,
      lastTama38Approval: null,
      tama38PlanTypes: [],
      policyConfidence: 'low',
      extractedFrom: 'iplan_analysis',
      analysisDate: new Date().toISOString()
    };
    
    if (!plans || plans.length === 0) {
      console.log('⚠️ No planning data available for TAMA 38 analysis');
      return policyData;
    }
    
    // Enhanced TAMA 38 keywords with variations
    const tama38Keywords = [
      // Official names
      'תמ"א 38', 'תמא 38', 'תמ״א 38', 'תמא38',
      'תוכנית מתאר ארצית 38',
      'national outline plan 38',
      'tama 38', 'tama38',
      
      // Common descriptions
      'חיזוק מבנים', 'חיזוק ועיבוי', 'חיזוק והוספה',
      'הוספת יחידות דיור', 'הוספת דירות',
      'חיזוק מבנה', 'חיזוק מבנה קיים',
      'חיזוק בניין', 'חיזוק ובניה',
      
      // Process-related terms
      'חיזוק לפי תמ"א', 'חיזוק לפי תמא',
      'פרויקט חיזוק', 'תכנית חיזוק',
      'רעידות אדמה', 'עמידות סיסמית'
    ];
    
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    
    let latestApprovalDate = null;
    const planTypes = new Set();
    
    // Analyze each plan for TAMA 38 indicators
    for (const plan of plans) {
      const planName = plan.title || plan.pl_name || '';
      const planObjectives = plan.objectives || plan.pl_objectives || '';
      const planType = plan.planType || plan.entity_subtype_desc || '';
      const combinedText = `${planName} ${planObjectives} ${planType}`.toLowerCase();
      
      // Check for TAMA 38 keywords
      const hasTama38Keyword = tama38Keywords.some(keyword => 
        combinedText.includes(keyword.toLowerCase())
      );
      
      if (hasTama38Keyword) {
        policyData.activeTama38Plans++;
        
        // Track plan types
        if (planType) {
          planTypes.add(planType);
        }
        
        // Check dates for recent activity
        const approvalDate = plan.approvalDate || plan.pl_date_8;
        const submissionDate = plan.submissionDate || plan.pl_date_advertise;
        
        if (approvalDate) {
          const planDate = new Date(approvalDate);
          if (planDate > twoYearsAgo) {
            policyData.recentTama38Activity = true;
          }
          
          // Track latest approval
          if (!latestApprovalDate || planDate > new Date(latestApprovalDate)) {
            latestApprovalDate = approvalDate;
          }
        } else if (submissionDate) {
          const planDate = new Date(submissionDate);
          if (planDate > twoYearsAgo) {
            policyData.recentTama38Activity = true;
          }
        }
        
        console.log(`🎯 TAMA 38 plan detected: "${planName}" - Status: ${plan.status || 'Unknown'} - Date: ${approvalDate || submissionDate || 'Unknown'}`);
      }
    }
    
    // Set policy status based on analysis
    policyData.tama38PlanTypes = Array.from(planTypes);
    policyData.lastTama38Approval = latestApprovalDate;
    
    // Determine policy status with confidence levels
    if (policyData.activeTama38Plans > 0 && policyData.recentTama38Activity) {
      policyData.tama38Status = YesNoUnknownValues.YES;
      policyData.policyConfidence = 'high';
      console.log(`✅ TAMA 38 ACTIVE: ${policyData.activeTama38Plans} plans with recent activity`);
    } else if (policyData.activeTama38Plans > 0) {
      policyData.tama38Status = YesNoUnknownValues.UNKNOWN;
      policyData.policyConfidence = 'medium';
      console.log(`⚠️ TAMA 38 UNCLEAR: ${policyData.activeTama38Plans} plans but no recent activity`);
    } else {
      // Check municipality patterns for areas likely to have TAMA 38
      const knownActiveMunicipalities = [
        'תל אביב-יפו', 'גבעתיים', 'רמת גן', 'חולון', 'בת ים', 'ירושלים'
      ];
      
      if (knownActiveMunicipalities.includes(municipalityName)) {
        policyData.tama38Status = YesNoUnknownValues.UNKNOWN;
        policyData.policyConfidence = 'low';
        console.log(`❓ TAMA 38 POSSIBLE: No plans in this area but ${municipalityName} typically has TAMA 38`);
      } else {
        policyData.tama38Status = YesNoUnknownValues.NO;
        policyData.policyConfidence = 'medium';
        console.log(`❌ TAMA 38 UNLIKELY: No plans found and municipality not typically active`);
      }
    }
    
    console.log(`📊 TAMA 38 Policy Analysis Results:`, {
      status: policyData.tama38Status,
      confidence: policyData.policyConfidence,
      activePlans: policyData.activeTama38Plans,
      recentActivity: policyData.recentTama38Activity,
      lastApproval: policyData.lastTama38Approval
    });
    
    return policyData;
  }
  
  /**
   * Extract betterment levy insights from planning documents
   * @param {Object[]} plans - iPLAN planning data
   * @param {string} municipalityName - Municipality name
   * @returns {Object} Betterment levy insights
   */
  static extractBettermentLevyInsights(plans = [], municipalityName) {
    console.log(`💰 Analyzing betterment levy patterns from ${plans.length} plans`);
    
    const levyInsights = {
      municipalityName,
      levyMentioned: false,
      levyExemptions: [],
      levyReductions: [],
      estimatedRate: null,
      confidence: 'low'
    };
    
    // Keywords that indicate levy discussions
    const levyKeywords = [
      'היטל השבחה', 'היטל', 'השבחה',
      'betterment levy', 'levy',
      'פטור מהיטל', 'פטור היטל',
      'הנחה בהיטל', 'הפחתת היטל',
      'תשלום היטל', 'חיוב היטל'
    ];
    
    for (const plan of plans) {
      const planName = plan.title || plan.pl_name || '';
      const planObjectives = plan.objectives || plan.pl_objectives || '';
      const combinedText = `${planName} ${planObjectives}`.toLowerCase();
      
      const hasLevyMention = levyKeywords.some(keyword => 
        combinedText.includes(keyword.toLowerCase())
      );
      
      if (hasLevyMention) {
        levyInsights.levyMentioned = true;
        console.log(`💰 Betterment levy mentioned in: "${planName}"`);
        
        // Look for exemption patterns
        if (combinedText.includes('פטור') || combinedText.includes('ללא היטל')) {
          levyInsights.levyExemptions.push(planName);
        }
        
        // Look for reduction patterns
        if (combinedText.includes('הנחה') || combinedText.includes('הפחתה')) {
          levyInsights.levyReductions.push(planName);
        }
      }
    }
    
    // Estimate levy rate based on municipality and patterns
    if (levyInsights.levyMentioned) {
      levyInsights.confidence = 'medium';
      
      // Municipality-specific patterns
      if (municipalityName === 'ירושלים' || levyInsights.levyExemptions.length > 0) {
        levyInsights.estimatedRate = 0.0; // Jerusalem typically has no levy
      } else if (municipalityName === 'תל אביב-יפו' || municipalityName === 'גבעתיים') {
        levyInsights.estimatedRate = 0.5; // Higher rates in central areas
      } else {
        levyInsights.estimatedRate = 0.25; // Standard rate
      }
    }
    
    console.log(`💰 Betterment Levy Analysis:`, levyInsights);
    return levyInsights;
  }
  
  /**
   * Get comprehensive municipality policy from iPLAN data
   * @param {Object[]} plans - iPLAN planning data
   * @param {string} municipalityName - Municipality name
   * @returns {Object} Complete policy analysis
   */
  static getComprehensiveMunicipalityPolicy(plans, municipalityName) {
    const tama38Policy = this.extractTama38Policy(plans, municipalityName);
    const levyInsights = this.extractBettermentLevyInsights(plans, municipalityName);
    
    return {
      ...tama38Policy,
      bettermentLevy: levyInsights,
      dataSource: 'iplan_extraction',
      analysisTimestamp: new Date().toISOString(),
      plansAnalyzed: plans.length
    };
  }
}

export default Tama38PolicyExtractor;
